import { Col, Divider, Form, Row, Select } from 'antd';
import { Rule } from 'antd/es/form';
import DropdownIcon from 'assets/icons/simulate-dropdown.svg';
import { OptionEntity } from 'domain/entities/OptionEntity';
import { openErrorNoti } from 'presentation/components/Notification';
import { FormInput } from 'presentation/components/form/FormInput.component';
import { FormRadio } from 'presentation/components/form/FormRadio.component';
import { FormTextArea } from 'presentation/components/form/FormTextArea.component';
import { completeFetching, startFetching } from 'presentation/redux/features/app/appSlice';
import { useAppDispatch } from 'presentation/redux/hooks';
import { useEffect, useState } from 'react';
import { COMMON, MASTER_CATEGORY_LIST, DELIVERY_ADDRESS } from 'utilities/constants/COMMON';
import getMessage from 'utilities/constants/MESSAGE';
import { PLACEHOLDER } from 'utilities/constants/PLACEHOLDER';
import { commonHelpers } from 'utilities/helpers';
import { StepProps } from '../props/StepProps';
import { ButtonList } from './ButtonList';
import { UserInfo } from './UserInfo';

const optionYear = Array.from(Array(51).keys()).map((item) => {
    return {
        label: item,
        value: item,
    };
});

const optionMonth = Array.from(Array(13).keys()).map((item) => {
    return {
        label: item,
        value: item,
    };
});

const INPUT_TEXT_MAX_LENGTH = 100;

export const PurchaseStepOne = ({
    data,
    masterData,
    next,
    setOfferId,
    setIsChange,
    ...props
}: StepProps) => {
    const [isShowAddress, setIsShowAddress] = useState<boolean>(false);
    const [btnDisable, setBtnDisable] = useState<boolean>(true);
    const [form] = Form.useForm();
    const dispatch = useAppDispatch();

    useEffect(() => {
        props.scrollTop && props.scrollTop();
    }, []);

    useEffect(() => {
        if (data && masterData) {
            //setDefaultvalue
            if (!data?.designatedAddressDiv) {
                const defaultOption = masterData[
                    MASTER_CATEGORY_LIST.ADDRESSEE_DIV_CLASSIFICATION
                ]?.find((item: OptionEntity) => item.isDefault);
                data.designatedAddressDiv = defaultOption?.value ?? '';
            }
            form.setFieldsValue({
                ...data,
                workplaceServiceYears: data.workplaceServiceYears || 0,
                workplaceServiceMonths: data.workplaceServiceMonths || 0,
            });
            onChangeForm();
        }
        if (data?.designatedAddressDiv == '99') {
            setIsShowAddress(true);
        }
    }, [data, masterData]);

    const generateNewMasterData = (masterData: any) => {
        let newMasterData = [];
        if (masterData[MASTER_CATEGORY_LIST.ADDRESSEE_DIV_CLASSIFICATION]) {
            newMasterData = masterData[MASTER_CATEGORY_LIST.ADDRESSEE_DIV_CLASSIFICATION].filter(
                (output: any) => {
                    return output.value != DELIVERY_ADDRESS.COMPANY;
                },
            );
        }
        return newMasterData;
    };
    const postcodeValidatorRule = (label?: string, message?: string): Rule => ({
        // @ts-expect-error custom id
        id: 'postcode',
        validator(_: any, value?: string) {
            const v = (value ?? '').replace(/\D/g, '');
            if (v.length !== 7)
                return Promise.reject(new Error(message ?? getMessage('Postcode_Invalid')));
            return Promise.resolve();
        },
    });

    const phoneValidatorRule = (label?: string, message?: string): Rule => ({
        // @ts-expect-error custom id
        id: 'phone',
        validator(_: any, value?: string) {
            const v = (value ?? '').replace(/\D/g, '');
            if (!v) return Promise.reject();
            if (v.length < 10 || v.length > 11) {
                return Promise.reject(new Error(message ?? '10〜11桁で入力してください'));
            }
            return Promise.resolve();
        },
    });

    const onChangeForm = () => {
        form.validateFields({
            validateOnly: true,
        })
            .then(() => {
                setBtnDisable(false);
            })
            .catch((err) => {
                setBtnDisable(err.errorFields.length > 0);
            });
    };

    const changeBtnDisable = (isError: any) => {
        if (isError) {
            setBtnDisable(true);
        } else {
            onChangeForm();
        }
    };

    const submitNext = async () => {
        try {
            dispatch(startFetching());
            const payload = {
                ...form.getFieldsValue(),
                designatedAddressPostalCode: isShowAddress
                    ? commonHelpers.toNumberOnly(form.getFieldValue('designatedAddressPostalCode'))
                    : '',
                designatedAddress: isShowAddress ? form.getFieldValue('designatedAddress') : '',
                designatedAddressNotes: isShowAddress
                    ? form.getFieldValue('designatedAddressNotes')
                    : '',
                homeEmail01: data?.homeEmail01,
                annualIncome: commonHelpers.toNumberOnly(form.getFieldValue('annualIncome')),
                memberName: data?.memberName,
                memberNameKana: data?.memberNameKana,
                birthday: data?.birthday,
                procedureId: data?.procedureId,
                purchaseOfferId: data?.purchaseOfferId ?? '',
            };
            const response = await props.purchaseUseCase.savePurchaseIndividualStep1(payload);
            if (response) {
                setOfferId && setOfferId(response.result?.purchaseOfferId ?? '');
                next && next();
            }
        } catch (error: any) {
            openErrorNoti();
        } finally {
            dispatch(completeFetching());
        }
    };

    return (
        <>
            <Form
                form={form}
                onValuesChange={() => {
                    onChangeForm();
                    setIsChange && setIsChange(true);
                }}
                onFinish={submitNext}
            >
                <Row className="min-w-min">
                    <UserInfo userInfo={data} />

                    <div className="w-full mb-[48px]">
                        <span className="content-step-form heading-step-form">自宅</span>
                        <Divider className="mb-6 my-1" />
                        <Row gutter={[0, 12]} className="mb-6">
                            <Col span={12} className="pr-[12px]">
                                <FormInput
                                    label="郵便番号（ハイフンなし）"
                                    name="homePostalCode"
                                    required
                                    maxLength={7}
                                    formatNumber={false}
                                    type="postcode"
                                    size={'large'}
                                    labelClassName="content-step-form label-step-form"
                                    rules={[postcodeValidatorRule('郵便番号')]}
                                />
                            </Col>
                            <Col span={12} className="pl-[12px]">
                                <FormInput
                                    label="住所"
                                    name="homeAddress"
                                    required
                                    maxLength={INPUT_TEXT_MAX_LENGTH}
                                    size={'large'}
                                    labelClassName="content-step-form label-step-form"
                                />
                            </Col>
                        </Row>
                        <Row gutter={[0, 12]} className="mb-6">
                            <Col span={12} className="pr-[12px]">
                                <span className="content-step-form label-step-form">
                                    メールアドレス１
                                </span>
                                <span className="content-step-form">
                                    {data?.homeEmail01 ?? COMMON.EMPTY}
                                </span>
                            </Col>
                            <Col span={12} className="pl-[12px]">
                                <FormInput
                                    label="メールアドレス2（任意）"
                                    name="homeEmail02"
                                    maxLength={255}
                                    type="email"
                                    size={'large'}
                                    labelClassName="content-step-form label-step-form"
                                    rules={[
                                        {
                                            type: 'email',
                                            message: getMessage('Mail_Invalid'),
                                        },
                                    ]}
                                />
                            </Col>
                        </Row>
                        <Row gutter={[0, 12]}>
                            <Col span={12} className="pr-[12px]">
                                <FormInput
                                    label="日中連絡の取れる電話番号（ハイフンなし）"
                                    name="homePhoneNumber01"
                                    type="phone"
                                    formatNumber={false}
                                    required
                                    maxLength={11}
                                    size={'large'}
                                    labelClassName="content-step-form label-step-form"
                                    rules={[phoneValidatorRule()]}
                                />
                            </Col>
                        </Row>
                    </div>

                    <div className="w-full mb-[48px]">
                        <span className="content-step-form heading-step-form">勤務先</span>
                        <Divider className="mb-6 my-1" />
                        <Row gutter={[0, 12]} className="mb-6">
                            <Col span={12} className="pr-[12px]">
                                <FormInput
                                    label="勤務先名称"
                                    name="workplaceName"
                                    required
                                    maxLength={INPUT_TEXT_MAX_LENGTH}
                                    size={'large'}
                                    labelClassName="content-step-form label-step-form"
                                />
                            </Col>
                            <Col span={12} className="pl-[12px]">
                                <FormInput
                                    label="勤務先の郵便番号（ハイフンなし）"
                                    name="workplacePostalCode"
                                    required
                                    maxLength={7}
                                    formatNumber={false}
                                    type="postcode"
                                    size={'large'}
                                    labelClassName="content-step-form label-step-form"
                                    rules={[
                                        postcodeValidatorRule('勤務先の郵便番号（ハイフンなし）'),
                                    ]}
                                />
                            </Col>
                        </Row>
                        <Row gutter={[0, 12]} className="mb-6">
                            <Col span={12} className="pr-[12px]">
                                <FormInput
                                    label="勤務先の所在地"
                                    name="workplaceAddress"
                                    required
                                    maxLength={INPUT_TEXT_MAX_LENGTH}
                                    size={'large'}
                                    labelClassName="content-step-form label-step-form"
                                />
                            </Col>
                            <Col span={12} className="pl-[12px]">
                                <FormInput
                                    label="勤務先での役職"
                                    name="workplaceRole"
                                    required
                                    maxLength={INPUT_TEXT_MAX_LENGTH}
                                    size={'large'}
                                    labelClassName="content-step-form label-step-form"
                                />
                            </Col>
                        </Row>
                        <Row gutter={[0, 12]} className="mb-6">
                            <Col span={12} className="pr-[12px]">
                                <FormInput
                                    label="勤務先の電話番号（ハイフンなし）"
                                    name="workplacePhoneNumber01"
                                    type="phone"
                                    formatNumber={false}
                                    required
                                    maxLength={11}
                                    size={'large'}
                                    labelClassName="content-step-form label-step-form"
                                    rules={[phoneValidatorRule()]}
                                />
                            </Col>
                            <Col span={12} className="pl-[12px]">
                                <span className="content-step-form label-step-form">
                                    勤続年数<span style={{ color: '#EB5757' }}>*</span>
                                </span>
                                <Row>
                                    <Col span={10}>
                                        <Form.Item name="workplaceServiceYears">
                                            <Select
                                                size="large"
                                                className="select-custom "
                                                defaultValue={0}
                                                suffixIcon={
                                                    <img src={DropdownIcon} alt="dropdown icon" />
                                                }
                                                options={optionYear}
                                                placeholder={PLACEHOLDER.SELECT}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col
                                        span={2}
                                        className="flex justify-center min-w-fit my-[5px] mt-[12px] "
                                    >
                                        年
                                    </Col>
                                    <Col span={9}>
                                        <Form.Item name="workplaceServiceMonths">
                                            <Select
                                                size="large"
                                                className="select-custom"
                                                defaultValue={0}
                                                options={optionMonth}
                                                suffixIcon={
                                                    <img src={DropdownIcon} alt="dropdown icon" />
                                                }
                                                placeholder={PLACEHOLDER.SELECT}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col
                                        span={3}
                                        className="flex justify-center min-w-fit my-[5px] mt-[12px] "
                                    >
                                        ヶ月
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                        <Row gutter={[0, 12]}>
                            <Col span={12} className="pr-[12px]">
                                <FormInput
                                    label="直近の年収"
                                    name="annualIncome"
                                    type="number"
                                    required
                                    maxLength={7}
                                    size={'large'}
                                    labelClassName="content-step-form label-step-form"
                                    suffix="万円"
                                />
                            </Col>
                        </Row>
                    </div>

                    <div className="w-full mb-[48px]">
                        <span className="content-step-form heading-step-form">送付先</span>
                        <Divider className="mb-6 my-1" />
                        <Row gutter={[0, 12]} className="mb-6">
                            <Col span={24} className="pr-[12px]">
                                <FormRadio
                                    label="送付先区分"
                                    name="designatedAddressDiv"
                                    options={masterData ? generateNewMasterData(masterData) : []}
                                    required
                                    onChange={(e) => {
                                        if (e === '99') {
                                            setIsShowAddress(true);
                                        } else {
                                            setIsShowAddress(false);
                                            form.setFields([
                                                { name: 'designatedAddressPostalCode', errors: [] },
                                                { name: 'designatedAddress', errors: [] },
                                                { name: 'designatedAddressNotes', errors: [] },
                                            ]);
                                        }
                                    }}
                                />
                            </Col>
                        </Row>
                        <Row gutter={[0, 12]} className="mb-6">
                            <Col span={12} className="pr-[12px]">
                                <FormInput
                                    label="送付先の郵便番号（ハイフンなし）"
                                    name="designatedAddressPostalCode"
                                    required={isShowAddress}
                                    disabled={!isShowAddress}
                                    type="postcode"
                                    formatNumber={false}
                                    maxLength={7}
                                    size={'large'}
                                    labelClassName="content-step-form label-step-form"
                                    rules={[
                                        postcodeValidatorRule('送付先の郵便番号（ハイフンなし）'),
                                    ]}
                                    currentForm={form}
                                    onValidateError={changeBtnDisable}
                                />
                            </Col>
                            <Col span={12} className="pl-[12px]">
                                <FormInput
                                    label="送付先の住所"
                                    name="designatedAddress"
                                    required={isShowAddress}
                                    disabled={!isShowAddress}
                                    maxLength={INPUT_TEXT_MAX_LENGTH}
                                    size={'large'}
                                    labelClassName="content-step-form label-step-form"
                                    currentForm={form}
                                    onValidateError={changeBtnDisable}
                                />
                            </Col>
                        </Row>
                        <Row gutter={[0, 12]}>
                            <Col span={24} className="pr-[12px]">
                                <FormTextArea
                                    label="送付先備考（任意）"
                                    name="designatedAddressNotes"
                                    maxLength={1000}
                                    rows={10}
                                    size={'large'}
                                    disabled={!isShowAddress}
                                    labelClassName="content-step-form label-step-form"
                                />
                            </Col>
                        </Row>
                    </div>
                    <ButtonList {...props} next={submitNext} disable={btnDisable} />
                </Row>
            </Form>
        </>
    );
};
